import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Star, Brain, Clock } from 'lucide-react';

export const ProgressPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    const docs = JSON.parse(localStorage.getItem('ecstatic_uploaded_documents') || '[]');
    const flashcards = JSON.parse(localStorage.getItem('ecstatic_uploaded_flashcards') || '[]');
    const quizzes = JSON.parse(localStorage.getItem('ecstatic_uploaded_quizzes') || '[]');
    const docActivities = docs.map((doc: any) => ({
      type: 'document',
      title: doc.fileName,
      subtitle: 'Uploaded and processed',
      time: doc.processedAt,
      icon: FileText
    }));
    const flashcardActivities = flashcards.map((fc: any) => ({
      type: 'flashcard',
      title: `Studied ${fc.count} flashcards`,
      subtitle: 'Flashcard session completed',
      time: fc.completedAt,
      icon: Star
    }));
    const quizActivities = quizzes.map((qz: any) => ({
      type: 'quiz',
      title: `Quiz - ${qz.score}%`,
      subtitle: `${qz.correctAnswers} out of ${qz.totalQuestions} correct`,
      time: qz.completedAt,
      icon: Brain
    }));
    const allActivities = [...docActivities, ...flashcardActivities, ...quizActivities].sort((a, b) => {
      return new Date(b.time).getTime() - new Date(a.time).getTime();
    });
    setRecentActivities(allActivities);
  }, []);

  return (
    <div className="h-full space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Progress</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Track your learning progress and recent activity</p>
      </div>
      
      {/* Stats Grid - Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400 mb-2" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{userProfile?.studyStats?.documentsUploaded || 0}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Documents</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Star className="h-5 w-5 text-accent-600 dark:text-accent-400 mb-2" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{userProfile?.studyStats?.flashcardsCreated || 0}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Flashcards</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Brain className="h-5 w-5 text-secondary-600 dark:text-secondary-400 mb-2" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{userProfile?.studyStats?.quizzesCompleted || 0}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Quizzes</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Clock className="h-5 w-5 text-success-600 dark:text-success-400 mb-2" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{userProfile?.studyStats?.studyMinutes || 0}m</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Study Time</div>
        </div>
      </div>
      
      {/* Recent Activity - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Recent Activity</h2>
        {recentActivities.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <p className="text-sm">No recent activity yet.</p>
            <p className="text-xs mt-1">Upload a document or start studying!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivities.slice(0, 8).map((activity, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <activity.icon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{activity.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{activity.subtitle}</p>
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {activity.time ? new Date(activity.time).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 